import { Controller } from "@hotwired/stimulus"

// Learner discovery map block (TMS-912): opens a node's panel over the map and
// tracks how many the learner has explored. The map is never unmounted while a
// panel is open, so closing one returns the learner to the ring exactly as they
// left it. Explored state is client-side only — it is a reading aid, not
// progress the course records.
export default class extends Controller {
  static targets = ["canvas", "node", "detail", "counter", "progressFill"]

  connect() {
    this.exploredIndices = new Set()
    this.openIndex = null
    this.renderProgress()
  }

  openNode(event) {
    event.preventDefault()

    const index = Number(event.currentTarget.dataset.nodeIndex)
    if (Number.isNaN(index)) return

    this.showDetail(index)
    this.exploredIndices.add(index)
    this.renderProgress()
  }

  closeDetail(event) {
    event?.preventDefault()
    this.hideDetail({ restoreFocus: true })
  }

  closeOnEscape() {
    if (this.openIndex === null) return

    this.hideDetail({ restoreFocus: true })
  }

  showDetail(index) {
    this.detailTargets.forEach((detail) => {
      detail.hidden = Number(detail.dataset.nodeIndex) !== index
    })
    this.nodeTargets.forEach((node) => {
      const selected = Number(node.dataset.nodeIndex) === index
      node.classList.toggle("is-open", selected)
      node.setAttribute("aria-expanded", selected ? "true" : "false")
    })

    this.openIndex = index
    this.element.classList.add("is-detail-open")
    this.detailFor(index)?.focus()
  }

  hideDetail({ restoreFocus }) {
    const previousIndex = this.openIndex

    this.detailTargets.forEach((detail) => { detail.hidden = true })
    this.nodeTargets.forEach((node) => {
      node.classList.remove("is-open")
      node.setAttribute("aria-expanded", "false")
    })

    this.openIndex = null
    this.element.classList.remove("is-detail-open")

    if (restoreFocus && previousIndex !== null) this.nodeFor(previousIndex)?.focus()
  }

  renderProgress() {
    const total = this.nodeTargets.length
    const explored = this.exploredIndices.size

    this.nodeTargets.forEach((node) => {
      node.classList.toggle("is-explored", this.exploredIndices.has(Number(node.dataset.nodeIndex)))
    })

    if (this.hasCounterTarget) this.counterTarget.textContent = `${explored} of ${total} explored`
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = total === 0 ? "0%" : `${(explored / total) * 100}%`
    }
  }

  detailFor(index) {
    return this.detailTargets.find((detail) => Number(detail.dataset.nodeIndex) === index)
  }

  nodeFor(index) {
    return this.nodeTargets.find((node) => Number(node.dataset.nodeIndex) === index)
  }
}
